/**
 * ArticleList — "On this page · <screen>": the help articles matching the current route (BR-10), at most
 * eight with a control for the rest. When no article matches, it renders the `fallback` the panel gives
 * it (search, categories and the Ask entry stay available, BR-11) instead of an empty list.
 *
 * Also home to the small building blocks every help list shares: the article row, the loading rows and
 * the error-with-retry block.
 */

import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { Button } from '../ui/button';
import { Skeleton } from '../ui/skeleton';
import { cn } from '@/lib/utils';
import { helpArticlePath, helpTopicPath, useScreenArticles } from '../../lib/api.help';
import { articleFocusKey, topicFocusKey } from './focus-keys';

/** At most this many articles are listed before "Show N more" (BR-10). */
const VISIBLE_LIMIT = 8;

/** The heading of a help article or topic (page h1, panel h2). */
export const HELP_HEADING_CLASS =
  'rounded text-xl font-bold leading-tight text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

export function HelpEyebrow({
  children,
  id,
  'data-testid': testId,
}: {
  children: ReactNode;
  id?: string;
  'data-testid'?: string;
}) {
  return (
    <h3 id={id} data-testid={testId} className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
      {children}
    </h3>
  );
}

interface HelpRowProps {
  /** Where the row goes when it is a normal link (a new tab, or outside the panel). */
  to: string;
  title: ReactNode;
  excerpt?: ReactNode;
  /** In the panel the row opens its target in place instead of navigating. */
  onOpen?: () => void;
  /** Lets the panel put keyboard focus back on this row when the view it opened closes. */
  focusKey: string;
  'data-testid': string;
}

function HelpRow({ to, title, excerpt, onOpen, focusKey, 'data-testid': testId }: HelpRowProps) {
  return (
    <Link
      to={to}
      data-testid={testId}
      data-focus-key={focusKey}
      onClick={(event) => {
        if (!onOpen || event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
        event.preventDefault();
        onOpen();
      }}
      className={cn(
        'flex min-h-[56px] items-center gap-3 border-b border-border px-1 py-3 transition-colors last:border-b-0',
        'hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
      )}
    >
      <div className="min-w-0 flex-1">
        <p className="break-words text-sm font-semibold text-foreground">{title}</p>
        {excerpt && <p className="mt-0.5 break-words text-[13px] text-muted-foreground">{excerpt}</p>}
      </div>
      <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
    </Link>
  );
}

interface ArticleRowProps {
  articleId: string;
  title: ReactNode;
  excerpt?: ReactNode;
  /** In the panel the row opens the article in place; without it the row is a normal link to /help/<id>. */
  onSelect?: (articleId: string) => void;
  'data-testid': string;
}

export function ArticleRow({ articleId, title, excerpt, onSelect, 'data-testid': testId }: ArticleRowProps) {
  return (
    <HelpRow
      to={helpArticlePath(articleId)}
      title={title}
      excerpt={excerpt}
      onOpen={onSelect && (() => onSelect(articleId))}
      focusKey={articleFocusKey(articleId)}
      data-testid={testId}
    />
  );
}

interface TopicRowProps {
  category: string;
  title: ReactNode;
  excerpt?: ReactNode;
  /** In the panel the row opens the topic in place; without it the row is a normal link to /help/topics/<category>. */
  onOpen?: () => void;
  'data-testid': string;
}

export function TopicRow({ category, title, excerpt, onOpen, 'data-testid': testId }: TopicRowProps) {
  return (
    <HelpRow
      to={helpTopicPath(category)}
      title={title}
      excerpt={excerpt}
      onOpen={onOpen}
      focusKey={topicFocusKey(category)}
      data-testid={testId}
    />
  );
}

export function RowsSkeleton({ rows = 3, 'data-testid': testId }: { rows?: number; 'data-testid': string }) {
  return (
    <div className="flex flex-col" data-testid={testId} aria-busy="true" aria-label="Loading">
      {Array.from({ length: rows }, (_, index) => (
        <div key={index} className="flex min-h-[56px] flex-col justify-center gap-2 border-b border-border px-1 py-3 last:border-b-0">
          <Skeleton className="h-4 w-2/3" />
          <Skeleton className="h-3 w-full" />
        </div>
      ))}
    </div>
  );
}

export function HelpLoadError({
  message,
  onRetry,
  'data-testid': testId,
}: {
  message: string;
  onRetry: () => void;
  'data-testid': string;
}) {
  return (
    <div
      role="alert"
      data-testid={testId}
      className="flex flex-col items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm"
    >
      <p className="text-destructive">{message}</p>
      <Button type="button" variant="outline" onClick={onRetry} data-testid={`${testId}-retry`}>
        Retry
      </Button>
    </div>
  );
}

interface ArticleListProps {
  pathname: string;
  /** The name of the screen ("Leases"), when known. */
  pageName: string | null;
  onSelectArticle?: (articleId: string) => void;
  /** Rendered instead of the list when no article matches the route. */
  fallback: ReactNode;
}

export function ArticleList({ pathname, pageName, onSelectArticle, fallback }: ArticleListProps) {
  const { data, isLoading, isError, refetch } = useScreenArticles(pathname, true);
  const [expanded, setExpanded] = useState(false);

  if (isLoading) return <RowsSkeleton data-testid="help-articles-loading" />;
  if (isError) {
    return (
      <HelpLoadError
        message="We couldn't load the help articles for this page."
        onRetry={() => void refetch()}
        data-testid="help-articles-error"
      />
    );
  }
  if (!data || data.length === 0) return <>{fallback}</>;

  const visible = expanded ? data : data.slice(0, VISIBLE_LIMIT);
  const hidden = data.length - visible.length;

  return (
    <section aria-labelledby="help-on-this-page" className="flex flex-col" data-testid="help-on-this-page">
      <HelpEyebrow id="help-on-this-page" data-testid="help-on-this-page-heading">
        {pageName ? `On this page · ${pageName}` : 'On this page'}
      </HelpEyebrow>
      <div className="flex flex-col">
        {visible.map((article) => (
          <ArticleRow
            key={article.id}
            articleId={article.id}
            title={article.title}
            excerpt={article.summary}
            onSelect={onSelectArticle}
            data-testid={`help-article-row-${article.id}`}
          />
        ))}
      </div>
      {hidden > 0 && (
        <Button
          type="button"
          variant="ghost"
          className="self-start text-primary"
          onClick={() => setExpanded(true)}
          data-testid="help-show-more"
        >
          Show {hidden} more
        </Button>
      )}
    </section>
  );
}
